import {Injectable} from '@angular/core';
import {BackendHttpService} from '../../core/backend-http.service';
import {apiEndpoints, platformIconPath} from '../../app.constants';
import {AddPostToProject} from '../../../models/influencer';
import {InfluencerService} from '../../services/influencer/influencer.service';
import {Observable} from 'rxjs/Observable';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {DiscoverInfluencer, DiscoverPost} from '../../../models/discover';
import {Subject} from 'rxjs/Subject';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';

@Injectable()
export class DiscoverService extends BackendHttpService<any> {
  constructor(protected router: Router,
              protected translate: TranslateService,
              private influencerService: InfluencerService) {

    super(router, translate);
  }

  /**
   * using to prevent infinitive scroll trigger loader next result when scrolling to last position
   */
  private _onScrollToPreviousPosition = false;
  private _cacheScrollPositionOnDiscover = true;
  private _isActiveDiscoverPage = false;
  /**
   * Since discover component will never destroyed after initializing.
   * So, this subject will be help to control behaviour whenever user leave discover page
   * @type {BehaviorSubject<boolean>}
   * @private
   */
  private _moveOutDiscoverSubject: Subject<boolean> = new BehaviorSubject<boolean>(false);

  getInfluencerGroupListSideBarMenu(companyId): Observable<any[]> {
  const apiUrl = apiEndpoints.getInfluencerGroupListSideBarMenu;
    return this.get(apiUrl, {selected_company_id: companyId}).map(res => res ? res.data.map((item) => ({
        id: item.influencer_group_id,
        name: item.influencer_group_name
      })) : []
    );
  }

  searchInfluencers(searchFilter): Observable<any> {
    const result = this.post(apiEndpoints.discoverSearchInfluencers, JSON.stringify(searchFilter));

    if (!result) {
      return Observable.of({
        influencers: [],
        lastSortValues: null,
        totalInfluencers: null
      });
    }

    return result.map(res => {
      if (!res.data.influencers) {
        res.data.influencers = [];
        return res.data;
      }

      res.data.influencers = res.data.influencers.map((item) => {
        const remappedItem = this.remapInfluencerResult(item);

        if (remappedItem.relativepath) {
          remappedItem.relativepath = remappedItem.relativepath.replace(/www.instagram.com\/|www.youtube.com\/user\//, '');
        }

        return remappedItem;
      });

      return res.data;
    });
  }

  /**
   * Remap new Discover result to old model
  */
  private remapInfluencerResult(newInfluencer: DiscoverInfluencer) {
    return {
      'id': newInfluencer.influencerId,
      'url': null,
      'url_hash': null,
      'impressions': newInfluencer.impression,
      'engagement': newInfluencer.engagement,
      'reach': newInfluencer.reach,
      'impact': newInfluencer.impact * 100,
      'keyword_id': 0,
      'keyword': null,
      'keyword_type_id': 0,
      'project_id': 0,
      'date': newInfluencer.urlDate,
      'datePublished': null,
      'topMention': (newInfluencer.topMentions) ? newInfluencer.topMentions.map(mention => mention.mention).join(', ') : '',
      'imageUrl': newInfluencer.profilePicture,
      'title': null,
      'blog_id': newInfluencer.influencerId,
      'urlType': 0,
      'domain': newInfluencer.domain,
      'influencerName': newInfluencer.influencerName,
      'influencer_id': newInfluencer.influencerId,
      'influencer_name': newInfluencer.influencerName,
      'influencer_image': newInfluencer.profilePicture,
      'influencer_country': null,
      'influencer_country_id': newInfluencer.country ? newInfluencer.country.id : null,
      'countryCode': newInfluencer.country ? newInfluencer.country.code : null,
      'country_code': newInfluencer.country ? newInfluencer.country.code : null,
      'topics': null,
      'verticals': null,
      'country': null,
      'rankingMetric': 0,
      'relativepath': (newInfluencer.channels && newInfluencer.channels.length > 0) ? newInfluencer.channels[0].relativePath : null,
      'platforms': (newInfluencer.channels && newInfluencer.channels.length > 0) ? this.getPlatformNames(newInfluencer.channels.map(c => c.platform.id)) : [],
      'influencerChannels': (newInfluencer.channels && newInfluencer.channels.length > 0) ? this.getPlatformNames(newInfluencer.channels.map(c => c.platform.id)) : [],
      'gender': newInfluencer.gender,
      'followers': newInfluencer.numberOfFollowers,
      'numberOfFollowers': newInfluencer.numberOfFollowers,
      'numberOfPosts': newInfluencer.numberOfPosts,
      'numOfPostThisMonth': 0, // Missing
      'favoriteTopics': newInfluencer.topTopics ? newInfluencer.topTopics.map(tt => tt.name).join(', ') : null,
      'channels': newInfluencer.channels
    };
  }

  searchPosts(searchFilter): Observable<any> {
    return this.post(apiEndpoints.discoverSearchPosts, JSON.stringify(searchFilter)).map(res => {
      // if the result is empty. Stop immediately
      if (!res.data.posts) {
        res.data.posts = [];
        return res.data;
      }
      // map the DiscoverPost to correct model
      res.data.posts = res.data.posts.map((item) => {
        return this.remapDiscoverPostResult(item);
      });

      return res.data;
    });
  }

  /**
   * Remap new Discover Post result to old model
   */
  private remapDiscoverPostResult(newPost: DiscoverPost) {
    return {
      blog_id: newPost.influencerId,
      channel: newPost.channel,
      country: null,
      countryCode: null,
      date: newPost.publishedDate,
      datePublished: null,
      domain: null,
      engagement: newPost.engagement,
      followers: null,
      gender: null,
      id: newPost.influencerId,
      imageUrl: newPost.imageUrl,
      videoUrl: newPost.videoUrl,
      impact: newPost.impact * 100,
      impressions: (newPost.platform && newPost.platform.id !== 2 ? newPost.impression : newPost.reach), // If platform is Instagram use reach as impression // TODO: Don't do it like this
      influencerName: newPost.influencerName,
      influencer_country: null,
      influencer_country_id: null,
      influencer_name: newPost.influencerName,
      keyword: null,
      keyword_id: 0,
      keyword_type_id: 0,
      mentions: newPost.mentions,
      numberOfPosts: null,
      platforms: null,
      project_id: 0,
      rankingMetric: 0,
      reach: newPost.reach,
      relativepath: newPost.relativePath || '',
      title: newPost.title,
      topics: null,
      url: newPost.fullUrl,
      urlType: 0,
      url_hash: newPost.urlHash,
      urlHash: newPost.urlHash,
      verticals: null,
      influencer_id: newPost.influencerId,
      numberOfFollowers: null,
      topMention: newPost.mentions ? newPost.mentions.join(', ') : '',
      platform: this.getPlatformName(newPost.platform ? newPost.platform.id : -1),
      platformId: newPost.platform ? newPost.platform.id : 0,
      handle: (newPost.relativePath || '').replace(/www.instagram.com\/|www.youtube.com\/user\//, ''),
      isStory: newPost.story,
      completed: this.influencerService.isInstagramStoryVideo(newPost) ? newPost.completed : true,
      isVideo: this.influencerService.isVideoPost(newPost),
      mediaWidth: newPost.mediaWidth,
      mediaHeight: newPost.mediaHeight,
      imageWidth: newPost.imageWidth,
      imageHeight: newPost.imageHeight,
      postType: newPost.postType
    };
  }
  /**
   * this method use to add multiple posts to project
   * @param influencerGroupAdding
   * @returns {Observable<any>}
   */
  public addPostsToProject(addPostToProject: AddPostToProject): Observable<any> {
    return this.post(apiEndpoints.addPostsManuallyIntoProject.replace(':project_id', String(addPostToProject.projectId)), {posts: addPostToProject.posts});
  }

  get isScrollingToPreviousPosition() {
    return this._onScrollToPreviousPosition;
  }

  onScrollToPreviousPosition(isScrolling) {
    this._onScrollToPreviousPosition = isScrolling;
  }

  get isAllowToCacheScrollPosition() {
    return this._cacheScrollPositionOnDiscover;
  }

  enableCachingScrollPosition() {
    this._cacheScrollPositionOnDiscover = true;
  }

  disableCachingScrollPosition() {
    this._cacheScrollPositionOnDiscover = false;
  }

  get isActiveDiscoverPage() {
    return this._isActiveDiscoverPage;
  }

  activeDiscoverPage() {
    this._isActiveDiscoverPage = true;
  }

  inactiveDiscoverPage() {
    this._isActiveDiscoverPage = false;
  }

  get moveOutDiscoverSubject(): Observable<boolean> {
    return this._moveOutDiscoverSubject.asObservable();
  }

  onLeaveDiscoverPage(isMoveOut: boolean) {
    this._moveOutDiscoverSubject.next(isMoveOut);
  }

  /**
   * Get platform name from platformId and set it as an array
   * @param platformId
   */
  private getPlatformName(platformId: number) {
    const platformNames = [];
    platformNames.push(this.influencerService.getPlatformNameById(platformId));
    return platformNames.join(', ');
  }

  private getPlatformNames(platformIds: Array<number>) {
    const platforms = this.filterDuplicatePlatforms(platformIds);
    const orderPlatforms = platforms.sort((p1, p2) => p1 - p2);
    const platformNames = orderPlatforms.map(
      (item) => this.influencerService.getPlatformNameById(item)
    );
    return platformNames.join(', ');
  }

  private filterDuplicatePlatforms(platformIds: Array<number>) {
    const platforms: Array<number> = [];
    platformIds.filter(item => {
      if (!platforms.includes(item)) {
        platforms.push(item);
      }
    });
    return platforms;
  }

}



// WEBPACK FOOTER //
// ./src/app/services/discover/discover.service.ts